/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

/* eslint-disable no-restricted-syntax, id-length, no-invalid-this */

'use strict';

// MODULES //

import setReadOnly from '@stdlib/utils-define-nonenumerable-read-only-property';
import setNonEnumerableReadOnlyAccessor from '@stdlib/utils-define-nonenumerable-read-only-accessor';
import isStructConstructorLike from '@stdlib/assert-is-struct-constructor-like';
import { isPrimitive as isNonNegativeInteger } from '@stdlib/assert-is-nonnegative-integer';
import isCollection from '@stdlib/assert-is-collection';
import isArrayBuffer from '@stdlib/assert-is-arraybuffer';
import isObject from '@stdlib/assert-is-object';
import hasProp from '@stdlib/assert-has-property';
import min from '@stdlib/math-base-special-fast-min';
import filled from '@stdlib/array-base-filled';
import ArrayBuffer from '@stdlib/array-buffer';
import DataView from '@stdlib/array-dataview';
import format from '@stdlib/error-tools-fmtprodmsg';
import PRIVATE_BUFFER from './private_buffer.js';
import CTOR_NAME from './ctor_name.js';
import createPrototypeAccessors from './create_prototype_accessors.js';
import isStructInstance from './is_struct_instance.js';
import normalize from './normalize_field_list.js';
import fieldNames from './field_names.js';
import fieldIndex from './field_index.js';
import resolveAlignment from './resolve_alignment.js';
import byteOffsets from './byte_offsets.js';
import partitions from './partitions.js';
import flatten from './flatten_fields.js';
import struct2string from './to_string.js';
import struct2json from './to_json.js';
import layoutFormat from './format_layout.js';


// MAIN //

/**
* Returns a constructor for creating a fixed-width composite data type (a.k.a., a `struct`).
*
* @param {ObjectArray} fields - structure fields
* @throws {TypeError} first argument must be an array-like object containing objects
* @throws {TypeError} field objects must have required properties
* @throws {TypeError} field objects must have valid fields
* @throws {TypeError} union types must be array-like objects containing objects
* @throws {RangeError} union types must contain fields having the same byte length
* @throws {TypeError} union types cannot contain nested union types
* @throws {TypeError} union types can only contain one field with a default value
* @throws {Error} unexpected error
* @returns {Function} constructor
*
* @example
* var fields = [
*     {
*         'type': 'union',
*         'fields': [
*             {
*                 'name': 'double',
*                 'description': 'double-precision floating-point number',
*                 'type': 'float64',
*                 'enumerable': true,
*                 'writable': true,
*                 'castingMode': 'none'
*             },
*             {
*                 'name': 'words',
*                 'description': 'high and low words',
*                 'type': 'uint32',
*                 'length': 2,
*                 'enumerable': true,
*                 'writable': true,
*                 'castingMode': 'none'
*             }
*         ]
*     }
* ];
* var Struct = factory( fields );
*
* var data = {
*     'double': 3.14
* };
* var s = new Struct( data );
* // returns <Struct>
*
* var v = s.double;
* // returns 3.14
*
* var w = s.words;
* // e.g., <Uint32Array>[ 1374389535, 1074339512 ]
*/
function factory( fields ) {
	var FIELD_NAMES;
	var BYTE_LENGTH;
	var PARTITIONS;
	var ALIGNMENT;
	var ACCESSORS;
	var FIELDS;
	var tmp;
	var o;
	if ( !isCollection( fields ) ) {
		throw new TypeError( format( '2KJ2O', fields ) );
	}
	// Normalize the list of field objects:
	tmp = normalize( fields );
	if ( tmp instanceof Error ) {
		throw tmp;
	}
	FIELDS = tmp;

	// Resolve the list of unique field names:
	tmp = fieldNames( FIELDS );
	if ( tmp instanceof Error ) {
		throw tmp;
	}
	FIELD_NAMES = tmp;

	// Resolve the struct's alignment requirements:
	ALIGNMENT = resolveAlignment( FIELDS );

	// Compute the byte offset for each field:
	FIELDS = byteOffsets( FIELDS, ALIGNMENT );

	// Now that we've finished processing provided field objects, flatten the list, such that union types are implied by field objects having the same byte offset:
	FIELDS = flatten( FIELDS );

	// Compute field "partitions" (i.e., non-overlapping views):
	PARTITIONS = partitions( FIELDS );

	// Compute the struct's byte length:
	o = FIELDS[ FIELDS.length-1 ];
	BYTE_LENGTH = o.byteOffset + o.byteLength + o.padding;

	/**
	* Constructor for a composite data type (a.k.a., a `struct`).
	*
	* @private
	* @param {(Object|ArrayBuffer)} [arg] - array buffer or data object
	* @param {NonNegativeInteger} [byteOffset=0] - offset, in bytes, to the first byte in a provided buffer for the returned `struct` to reference
	* @param {NonNegativeInteger} [byteLength] - maximum number of elements in the byte array
	* @throws {RangeError} must provide sufficient memory to accommodate byte offset and view length requirements
	* @throws {TypeError} second argument must be a nonnegative integer
	* @throws {TypeError} third argument must be a nonnegative integer
	* @throws {Error} union types may only be initialized by a single member
	* @returns {Struct} struct instance
	*/
	function Struct( arg, byteOffset, byteLength ) {
		var values;
		var nargs;
		var cache;
		var view;
		var obj;
		var o;
		var v;
		var i;
		var j;
		var k;

		nargs = arguments.length;
		if ( !( this instanceof Struct ) ) {
			if ( nargs === 0 ) {
				return new Struct();
			}
			if ( nargs === 1 ) {
				return new Struct( arg );
			}
			if ( nargs === 2 ) {
				return new Struct( arg, byteOffset );
			}
			return new Struct( arg, byteOffset, byteLength );
		}
		if ( isArrayBuffer( arg ) ) {
			if ( nargs === 1 ) {
				view = new DataView( arg, 0, BYTE_LENGTH );
			} else {
				if ( !isNonNegativeInteger( byteOffset ) ) {
					throw new TypeError( format( '2KJ2C', byteOffset ) );
				}
				if ( nargs === 2 ) {
					view = new DataView( arg, byteOffset, BYTE_LENGTH );
				} else {
					if ( !isNonNegativeInteger( byteLength ) ) {
						throw new TypeError( format( '2KJIl', byteLength ) );
					}
					view = new DataView( arg, byteOffset, min( byteLength, BYTE_LENGTH ) ); // eslint-disable-line max-len
				}
			}
			if ( view.byteLength < BYTE_LENGTH ) {
				throw new RangeError( format( '2KJIm', BYTE_LENGTH ) );
			}
		} else {
			view = new DataView( new ArrayBuffer( BYTE_LENGTH ) );
			if ( nargs > 0 ) {
				if ( !isObject( arg ) ) {
					throw new TypeError( format( '2KJ3L', arg ) );
				}
				obj = arg;
			}
		}
		// Bind the byte buffer to the current instance:
		setReadOnly( this, PRIVATE_BUFFER, view );

		// If we were provided a data object, set provided fields...
		if ( obj !== void 0 ) {
			// Initialize an array containing values to set:
			values = filled( void 0, FIELDS.length );

			// For each field, determine whether a value has been specified...
			cache = {};
			for ( i = 0; i < FIELDS.length; i++ ) {
				k = FIELD_NAMES[ i ];
				j = PARTITIONS[ i ];
				if ( hasProp( obj, k ) ) {
					// Check whether a value has already been specified for this view (i.e., via another union member)...
					if ( cache[ j ] ) {
						throw new Error( format('2KJIU') );
					}
					values[ i ] = obj[ k ];
					cache[ j ] = true;
				}
			}
			// Perform a second pass over the fields to fill in default initial values...
			for ( i = 0; i < FIELDS.length; i++ ) {
				j = PARTITIONS[ i ];
				if ( cache[ j ] ) {
					// Skip fields already having initial values...
					continue;
				}
				o = FIELDS[ i ];
				if ( o.default !== void 0 ) {
					values[ i ] = o.default;
				}
			}
			// Set all fields with initialization values...
			for ( i = 0; i < FIELDS.length; i++ ) {
				v = values[ i ];
				if ( v !== void 0 ) {
					ACCESSORS[ FIELD_NAMES[ i ] ][ 1 ].call( this, v );
				}
			}
		}
		return this;
	}

	/**
	* Constructor name.
	*
	* @private
	* @name name
	* @memberof Struct
	* @readonly
	* @type {string}
	* @default 'Struct'
	*/
	setReadOnly( Struct, 'name', CTOR_NAME );

	/**
	* Alignment.
	*
	* @private
	* @name alignment
	* @memberof Struct
	* @readonly
	* @type {PositiveInteger}
	*/
	setReadOnly( Struct, 'alignment', ALIGNMENT );

	/**
	* Size (in bytes) of the `struct`.
	*
	* @private
	* @name byteLength
	* @memberof Struct
	* @readonly
	* @type {PositiveInteger}
	*/
	setReadOnly( Struct, 'byteLength', BYTE_LENGTH );

	/**
	* Returns a list of `struct` fields.
	*
	* @private
	* @name fields
	* @memberof Struct
	* @readonly
	* @type {Array<string>}
	*/
	setNonEnumerableReadOnlyAccessor( Struct, 'fields', function get() {
		return FIELD_NAMES.slice();
	});

	/**
	* Returns a string corresponding to the `struct` layout.
	*
	* @private
	* @name layout
	* @memberof Struct
	* @readonly
	* @type {string}
	*/
	setNonEnumerableReadOnlyAccessor( Struct, 'layout', function get() {
		// As this is not likely to be a commonly used API, intentionally recompute the layout format in order to avoid consuming memory for what could be a long string...
		return layoutFormat( FIELDS );
	});

	/**
	* Returns the underlying byte buffer of a `struct`.
	*
	* @private
	* @name bufferOf
	* @memberof Struct
	* @readonly
	* @type {Function}
	* @param {Object} obj - struct instance
	* @throws {TypeError} must provide a `struct` instance
	* @returns {ArrayBuffer} underlying byte buffer
	*/
	setReadOnly( Struct, 'bufferOf', function bufferOf( obj ) {
		if ( !isStructInstance( obj ) ) {
			throw new TypeError( format( '2KJIn', obj ) );
		}
		return obj[ PRIVATE_BUFFER ].buffer;
	});

	/**
	* Returns the length, in bytes, of the value specified by the provided field name.
	*
	* @private
	* @name byteLengthOf
	* @memberof Struct
	* @readonly
	* @type {Function}
	* @param {string} name - field name
	* @throws {Error} struct must have at least one field
	* @throws {TypeError} must provide a recognized field name
	* @returns {NonNegativeInteger} byte length
	*/
	setReadOnly( Struct, 'byteLengthOf', function byteLengthOf( name ) {
		var idx = fieldIndex( FIELD_NAMES, name );
		if ( idx instanceof Error ) {
			throw idx;
		}
		return FIELDS[ idx ].byteLength;
	});

	/**
	* Returns the offset, in bytes, from the beginning of a `struct` to the value specified by the provided field name.
	*
	* @private
	* @name byteOffsetOf
	* @memberof Struct
	* @readonly
	* @type {Function}
	* @param {string} name - field name
	* @throws {Error} struct must have at least one field
	* @throws {TypeError} must provide a recognized field name
	* @returns {NonNegativeInteger} byte offset
	*/
	setReadOnly( Struct, 'byteOffsetOf', function byteOffsetOf( name ) {
		var idx = fieldIndex( FIELD_NAMES, name );
		if ( idx instanceof Error ) {
			throw idx;
		}
		return FIELDS[ idx ].byteOffset;
	});

	/**
	* Returns the description associated with a provided field name.
	*
	* @private
	* @name descriptionOf
	* @memberof Struct
	* @readonly
	* @type {Function}
	* @param {string} name - field name
	* @throws {Error} struct must have at least one field
	* @throws {TypeError} must provide a recognized field name
	* @returns {string} description
	*/
	setReadOnly( Struct, 'descriptionOf', function descriptionOf( name ) {
		var idx = fieldIndex( FIELD_NAMES, name );
		if ( idx instanceof Error ) {
			throw idx;
		}
		return FIELDS[ idx ].description;
	});

	/**
	* Returns a boolean indicating whether a provided value is a `struct` instance.
	*
	* @private
	* @name isStruct
	* @memberof Struct
	* @readonly
	* @type {Function}
	* @param {*} value - input value
	* @returns {boolean} boolean indicating whether a value is a `struct` instance
	*/
	setReadOnly( Struct, 'isStruct', function isStruct( value ) {
		return (
			value instanceof Struct ||
			(
				isStructInstance( value ) &&
				isStructConstructorLike( value.constructor )
			)
		);
	});

	/**
	* Returns the type associated with a provided field name.
	*
	* @private
	* @name typeOf
	* @memberof Struct
	* @readonly
	* @type {Function}
	* @param {string} name - field name
	* @throws {Error} struct must have at least one field
	* @throws {TypeError} must provide a recognized field name
	* @returns {(string|Object)} type
	*/
	setReadOnly( Struct, 'typeOf', function typeOf( name ) {
		var idx = fieldIndex( FIELD_NAMES, name );
		if ( idx instanceof Error ) {
			throw idx;
		}
		return FIELDS[ idx ].type;
	});

	/**
	* Returns the underlying byte buffer of a `struct` as a `DataView`.
	*
	* @private
	* @name viewOf
	* @memberof Struct
	* @readonly
	* @type {Function}
	* @param {Object} obj - struct instance
	* @throws {TypeError} must provide a `struct` instance
	* @returns {DataView} view of underlying byte buffer
	*/
	setReadOnly( Struct, 'viewOf', function viewOf( obj ) {
		var buf;
		if ( !isStructInstance( obj ) ) {
			throw new TypeError( format( '2KJIn', obj ) );
		}
		buf = obj[ PRIVATE_BUFFER ];
		return new DataView( buf.buffer, buf.byteOffset, buf.byteLength );
	});

	// Create prototype accessors for getting and setting field values:
	ACCESSORS = createPrototypeAccessors( Struct.prototype, FIELDS );

	/**
	* Serializes a `struct` to a string.
	*
	* @private
	* @name toString
	* @memberof Struct.prototype
	* @readonly
	* @type {Function}
	* @param {Options} [options] - function options
	* @param {string} [options.format='none'] - serialization format
	* @throws {Error} `this` must be a struct instance
	* @throws {TypeError} options argument must be an object
	* @throws {TypeError} must provide valid options
	* @returns {string} serialized `struct` instance
	*/
	setReadOnly( Struct.prototype, 'toString', function toString() {
		var opts;
		if ( !( this instanceof Struct ) ) {
			throw new Error( format('2KJIV') );
		}
		if ( arguments.length > 0 ) {
			opts = arguments[ 0 ];
		} else {
			opts = {};
		}
		return struct2string( Struct, FIELDS, opts );
	});

	/**
	* Serializes a `struct` to JSON.
	*
	* @private
	* @name toJSON
	* @memberof Struct.prototype
	* @readonly
	* @type {Function}
	* @throws {Error} `this` must be a struct instance
	* @returns {JSON} serialized `struct` instance
	*/
	setReadOnly( Struct.prototype, 'toJSON', function toJSON() {
		if ( !( this instanceof Struct ) ) {
			throw new Error( format('2KJIV') );
		}
		return struct2json( this, FIELDS );
	});

	return Struct;
}


// EXPORTS //

export default factory;
